import React, { Suspense, useEffect, useMemo, useState } from 'react';
import AutomotiveBreadcrumbs from '../automotivePage/AutomotiveBreadcrumbs';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ComponentName, useIndustrialComponents } from './IndustrialDisplayComponent';
import { ApplicationsContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import { buildSlugMaps } from '../../utils/slug';

const toCamelCase = (str: string) => {
    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
};

interface IndustrialOverviewPageProps {
    contentTree: ApplicationsContent;
}

const INDUSTRIAL_ROOT_NAME: ComponentName = 'industrial'; // internal key in components
const INDUSTRIAL_BASE_PATH = '/applications/industrial'; // route segment root

const IndustrialDisplay: React.FC<IndustrialOverviewPageProps> = ({ contentTree }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { components } = useIndustrialComponents(contentTree);

    /* ---------- slug helpers ---------- */
    const { nameToSlug, slugToName } = useMemo(() => buildSlugMaps(components), [components]);

    const [componentStack, setComponentStack] = useState<ComponentName[]>([INDUSTRIAL_ROOT_NAME]);

    /* ---------- sync stack <- URL ---------- */
    useEffect(() => {
        const pathSegments = location.pathname.toLowerCase().split('/').filter(Boolean);

        // find the segment that matches the root slug (fallback: literal "industrial")
        // prefer slug from map so that if root slug ever changes, parsing still works
        const rootSlug = nameToSlug[INDUSTRIAL_ROOT_NAME] ?? 'industrial';
        const rootIdx = pathSegments.indexOf(rootSlug);

        let relevant: string[] = [];
        if (rootIdx !== -1) {
            relevant = pathSegments.slice(rootIdx + 1);
        }

        if (relevant.length === 0) {
            setComponentStack([INDUSTRIAL_ROOT_NAME]);
            return;
        }

        const names: ComponentName[] = relevant.map((seg) => {
            const n = slugToName[seg];
            return (n as ComponentName) ?? INDUSTRIAL_ROOT_NAME;
        });

        setComponentStack([INDUSTRIAL_ROOT_NAME, ...names]);
    }, [location.pathname, nameToSlug, slugToName]);

    /* ---------- push ---------- */
    const handleComponentSwitch = (rawName: ComponentName | string) => {
        // accept either internal name or slug
        const internal = (rawName in nameToSlug ? rawName : slugToName[rawName]) as ComponentName;
        if (!internal) return; // unknown; ignore

        const newStack = [...componentStack, internal];
        setComponentStack(newStack);

        const slugPath = newStack
            .slice(1)
            .map((n) => nameToSlug[n])
            .filter(Boolean)
            .join('/');
        navigate(`${INDUSTRIAL_BASE_PATH}/${slugPath}`, { replace: false });
    };

    /* ---------- pop ---------- */
    const handleBack = () => {
        if (componentStack.length > 1) {
            const newStack = componentStack.slice(0, -1);
            setComponentStack(newStack);

            const slugPath = newStack
                .slice(1)
                .map((n) => nameToSlug[n])
                .filter(Boolean)
                .join('/');
            navigate(`${INDUSTRIAL_BASE_PATH}/${slugPath}`, { replace: false });
        } else {
            setComponentStack([INDUSTRIAL_ROOT_NAME]);
            navigate(INDUSTRIAL_BASE_PATH, { replace: false });
        }
    };

    /* ---------- breadcrumbs jump ---------- */
    const handleBreadcrumbClick = (index: number) => {
        const newStack = componentStack.slice(0, index + 1);
        setComponentStack(newStack);

        const slugPath = newStack
            .slice(1)
            .map((n) => nameToSlug[n])
            .filter(Boolean)
            .join('/');
        navigate(`${INDUSTRIAL_BASE_PATH}/${slugPath}`, { replace: false });
    };

    const fadeVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    const fadeTransition = {
        duration: 0.8,
        ease: 'easeInOut',
    };

    const currentComponentName = componentStack[componentStack.length - 1];

    const currentComponentInfo = currentComponentName
        ? components[currentComponentName]
        : undefined;

    const breadcrumbItems = componentStack.map((componentName, index) => ({
        label: (components[componentName]?.label || componentName).toString(),
    }));

    const commonProps = {
        onBack: handleBack,
        onAnnotationClick: handleComponentSwitch,
    };

    return (
        <>
            <AutomotiveBreadcrumbs
                items={breadcrumbItems}
                onBreadcrumbClick={handleBreadcrumbClick}
            />
            <Typography
                sx={{ marginBottom: '2rem', fontFamily: 'Poppins', fontSize: '16px' }}
            ></Typography>
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentComponentName}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={fadeVariants}
                    transition={fadeTransition}
                >
                    <Suspense fallback={<div>Loading...</div>}>
                        {currentComponentInfo?.imageComponent ? (
                            <currentComponentInfo.imageComponent onBack={handleBack} />
                        ) : currentComponentInfo?.component ? (
                            <currentComponentInfo.component {...commonProps} />
                        ) : null}
                    </Suspense>
                </motion.div>
            </AnimatePresence>
        </>
    );
};

export default IndustrialDisplay;
